import { getOriginLoginType, getUserId, hasAuthSession } from '@/utils/auth-token';
import { getAuthGeneration, registerAuthTeardown } from '@/utils/auth-teardown';

const REGISTER_TYPES = new Set(['EMAIL', 'GOOGLE', 'WALLET']);

let cachedGeneration = -1;
let cachedUserId = '';
let cachedRegisterType = '';
let profileLoaded = false;
let inflightProfile = null;
let inflightGeneration = -1;
let inflightUserId = '';

export function normalizeUserRegisterType(value) {
  const normalized = String(value ?? '')
    .trim()
    .toUpperCase();
  return REGISTER_TYPES.has(normalized) ? normalized : '';
}

function currentSessionKey() {
  return {
    generation: getAuthGeneration(),
    userId: String(getUserId() ?? '').trim(),
  };
}

function isCurrentSession({ generation, userId }) {
  const current = currentSessionKey();
  return current.generation === generation && current.userId === userId && hasAuthSession();
}

export function captureUserSessionProfileRequest() {
  return currentSessionKey();
}

export function noteUserSessionProfileLoaded(profile, expectedSession = null) {
  if (!hasAuthSession()) return '';
  if (expectedSession && !isCurrentSession(expectedSession)) return '';
  const session = currentSessionKey();
  cachedGeneration = session.generation;
  cachedUserId = session.userId;
  cachedRegisterType = normalizeUserRegisterType(profile?.registerType);
  profileLoaded = true;
  return cachedRegisterType;
}

export function getSessionRegisterType() {
  const session = currentSessionKey();
  if (
    !profileLoaded ||
    cachedGeneration !== session.generation ||
    cachedUserId !== session.userId
  ) {
    return '';
  }
  return cachedRegisterType;
}

/**
 * 当前登录轨：本次登录写入的 originLoginType 优先。
 * registerType 只是首次注册来源（合并账号下不会随切换登录改写），
 * 仅在 origin 缺失（旧会话/异常）时作兜底，避免 Google 注册用户改用钱包登录仍被当成托管轨。
 */
export function getSessionLoginType() {
  return getOriginLoginType() || getSessionRegisterType();
}

export function isSessionWalletLogin() {
  return getSessionLoginType() === 'WALLET';
}

/**
 * 每个认证代次只主动拉取一次 profile。失败不落错误缓存，后续资金操作可以重试。
 * @returns {Promise<'EMAIL'|'GOOGLE'|'WALLET'|''>}
 */
export async function ensureUserSessionProfile() {
  if (!hasAuthSession()) return '';
  const session = currentSessionKey();
  const cached = getSessionRegisterType();
  if (profileLoaded && cachedGeneration === session.generation && cachedUserId === session.userId) {
    return cached;
  }
  if (
    inflightProfile &&
    inflightGeneration === session.generation &&
    inflightUserId === session.userId
  ) {
    return inflightProfile;
  }

  const task = import('@/api/user')
    .then(({ getUserProfile }) => getUserProfile())
    .then((profile) => {
      if (!isCurrentSession(session)) return '';
      return noteUserSessionProfileLoaded(profile);
    })
    .catch((error) => {
      if (isCurrentSession(session)) {
        console.warn('[auth] user profile registration type unavailable', error);
      }
      return '';
    })
    .finally(() => {
      if (inflightProfile !== task) return;
      inflightProfile = null;
      inflightGeneration = -1;
      inflightUserId = '';
    });

  inflightProfile = task;
  inflightGeneration = session.generation;
  inflightUserId = session.userId;
  return task;
}

export function resetUserSessionProfile() {
  cachedGeneration = -1;
  cachedUserId = '';
  cachedRegisterType = '';
  profileLoaded = false;
  inflightProfile = null;
  inflightGeneration = -1;
  inflightUserId = '';
}

registerAuthTeardown(resetUserSessionProfile);
